import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, BarChart2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { getPlaybookAnalytics } from '../../services/playbookService';
import PnlBreakdownChart from './widgets/analytics/PnlBreakdownChart';
import RulePerformanceWidget from './widgets/analytics/RulePerformanceWidget';
import RuleCombinationHeatmap from './widgets/analytics/RuleCombinationHeatmap';
import DisciplineOverTime from './widgets/analytics/DisciplineOverTime';
import PlaybookROI from './widgets/analytics/PlaybookROI';

const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <span className="h-3.5 w-0.5 rounded-full bg-primary inline-block" />
    <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
      {children}
    </span>
  </div>
);

const PlaybookAnalytics = ({ playbooks = [] }) => {
  const [selectedId, setSelectedId] = useState(playbooks[0]?.id ?? null);

  // Widget 1: all-playbook PnL breakdown
  const [w1Data, setW1Data] = useState([]);
  const [w1Loading, setW1Loading] = useState(true);
  const [w1Error, setW1Error] = useState(null);

  // Per-playbook widget states
  const [w2Data, setW2Data] = useState([]);
  const [w2Loading, setW2Loading] = useState(false);

  const [w3Data, setW3Data] = useState([]);
  const [w3Loading, setW3Loading] = useState(false);

  const [w4Data, setW4Data] = useState({ data: [], rules: [] });
  const [w4Loading, setW4Loading] = useState(false);

  const [w5Data, setW5Data] = useState(null);
  const [w5Loading, setW5Loading] = useState(false);

  const [w6Data, setW6Data] = useState(null);
  const [w6Loading, setW6Loading] = useState(false);

  const [w7Data, setW7Data] = useState(null);
  const [w7Loading, setW7Loading] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  // ─── Widget 1 ───
  const fetchW1 = useCallback(async () => {
    setW1Loading(true);
    setW1Error(null);
    try {
      const res = await getPlaybookAnalytics('pnl_breakdown', null);
      if (res.success) setW1Data(res.data || []);
      else setW1Error(res.error || 'Failed to load');
    } catch (err) {
      setW1Error(err.message);
    } finally {
      setW1Loading(false);
    }
  }, []);

  // ─── Widgets 2-7 per playbook ───
  const fetchPerPlaybook = useCallback(async (playbookId) => {
    if (!playbookId) return;
    setW2Loading(true); setW3Loading(true); setW4Loading(true);
    setW5Loading(true); setW6Loading(true); setW7Loading(true);

    const [r2, r3, r4, r5, r6, r7] = await Promise.allSettled([
      getPlaybookAnalytics('rule_adherence', playbookId),
      getPlaybookAnalytics('rule_impact', playbookId),
      getPlaybookAnalytics('rule_combinations', playbookId),
      getPlaybookAnalytics('cost_of_breaking', playbookId),
      getPlaybookAnalytics('discipline_over_time', playbookId),
      getPlaybookAnalytics('fit_score', playbookId),
    ]);

    if (r2.status === 'fulfilled' && r2.value.success) setW2Data(r2.value.data || []);
    setW2Loading(false);

    if (r3.status === 'fulfilled' && r3.value.success) setW3Data(r3.value.data || []);
    setW3Loading(false);

    if (r4.status === 'fulfilled' && r4.value.success) {
      setW4Data({ data: r4.value.data?.data || [], rules: r4.value.data?.rules || [] });
    }
    setW4Loading(false);

    if (r5.status === 'fulfilled' && r5.value.success) setW5Data(r5.value.data);
    setW5Loading(false);

    if (r6.status === 'fulfilled' && r6.value.success) setW6Data(r6.value.data);
    setW6Loading(false);

    if (r7.status === 'fulfilled' && r7.value.success) setW7Data(r7.value.data);
    setW7Loading(false);
  }, []);

  useEffect(() => { fetchW1(); }, [fetchW1]);
  useEffect(() => { fetchPerPlaybook(selectedId); }, [selectedId, fetchPerPlaybook]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchW1(), fetchPerPlaybook(selectedId)]);
    setRefreshing(false);
  };

  if (playbooks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <BarChart2 className="w-10 h-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm text-muted-foreground">No playbooks yet.</p>
        <p className="text-xs text-muted-foreground/60 mt-1">
          Create a playbook and journal some trades to see analytics.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Playbook and rule performance based on journaled trades.
        </p>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-muted hover:bg-muted/80 text-muted-foreground transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ── Section 1: Playbook Comparison (all playbooks) ── */}
      <section>
        <SectionLabel>Playbook Comparison</SectionLabel>
        {w1Error ? (
          <p className="text-sm text-red-400">{w1Error}</p>
        ) : (
          <PnlBreakdownChart data={w1Data} loading={w1Loading} />
        )}
      </section>

      {/* ── Playbook selector ── */}
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground shrink-0">Analysing playbook:</span>
        <Select value={selectedId ?? ''} onValueChange={setSelectedId}>
          <SelectTrigger className="w-48 h-8 text-xs">
            <SelectValue placeholder="Select playbook" />
          </SelectTrigger>
          <SelectContent>
            {playbooks.map((pb) => (
              <SelectItem key={pb.id} value={pb.id} className="text-xs">
                {pb.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* ── Section 2: Rule Analysis ── */}
      <section>
        <SectionLabel>Rule Analysis</SectionLabel>
        <div className="space-y-6">
          <RulePerformanceWidget
            adherenceData={w2Data}
            impactData={w3Data}
            adherenceLoading={w2Loading}
            impactLoading={w3Loading}
          />
          <RuleCombinationHeatmap
            data={w4Data.data}
            rules={w4Data.rules}
            loading={w4Loading}
          />
        </div>
      </section>

      {/* ── Section 3: Discipline & Behavior ── */}
      <section>
        <SectionLabel>Discipline &amp; Behavior</SectionLabel>
        <div className="space-y-6">
          <DisciplineOverTime data={w6Data} loading={w6Loading} />
          <PlaybookROI
            costData={w5Data}
            fitData={w7Data}
            costLoading={w5Loading}
            fitLoading={w7Loading}
          />
        </div>
      </section>
    </div>
  );
};

export default PlaybookAnalytics;
